/**
 * 设备命令构造器
 * 对齐旧版 js/server-1.js 中的命令协议
 *
 * 协议格式: AA 53 LENGTH COMMAND DATA... CHECKSUM
 * - 帧头: AA 53
 * - 长度: 从命令字到校验和的字节数
 * - 命令字: 1字节
 * - 数据: 变长
 * - 校验和: 帧头+长度+命令+数据的和，取反+1，取低8位
 */

import { hexStringToBytes } from './protocol'

/**
 * 设备命令代码
 * 与原项目 js/server-1.js 中的命令代码保持一致
 */
export enum DeviceCommand {
  // Status commands
  GET_STATUS = 0x80,
  GET_DEVICE_DETAIL = 0x82,

  // EQ commands (module 0x52)
  EQ_MODULE = 0x52,
  EQ_SUBMODE_SINGLE = 0x02,  // 单点设置
  EQ_SUBMODE_SEGMENT1 = 0x15, // 分段设置 - 第1段 (0-3频点)
  EQ_SUBMODE_SEGMENT2 = 0x16, // 分段设置 - 第2段 (4-7频点)
  EQ_SUBMODE_SEGMENT3 = 0x17, // 分段设置 - 第3段 (8-11频点)
  EQ_SUBMODE_SEGMENT4 = 0x18, // 分段设置 - 第4段 (12-13频点)

  // Mute commands
  SET_SPEAKER_MUTE = 0x54,  // 喇叭静音
  SET_MIC_MUTE = 0x55,      // 麦克风静音

  // Volume commands
  SET_SPEAKER_VOLUME = 0x56, // 喇叭音量
  SET_MIC_VOLUME = 0x57,       // 麦克风音量

  // Other feature commands
  SET_ANTI_INTERFERENCE = 0x58, // 抗干扰
  SET_ENC = 0x51,               // AI降噪 (ENC)
  SET_RGB = 0x60,               // RGB灯效
  SET_VOICE_EFFECT = 0x61,      // 魔幻音效

  // Power control
  SET_POWER = 0x70,             // 关机/开机

  // Audio mix/split
  SET_AUDIO_SPLIT = 0x5B,       // 音频分离开关 (0x01)
  SET_AUDIO_BALANCE = 0x5B,     // 音频混合比例 (0x00)
}

// Frame header (AA 53)
const FRAME_HEADER: [number, number] = [0xAA, 0x53]

/**
 * Calculate checksum for protocol frame
 * Algorithm: Sum of header + all data bytes, then 16-bit complement
 */
export function calculateChecksum(data: number[]): string {
  let sum = FRAME_HEADER[0] + FRAME_HEADER[1]

  for (const byte of data) {
    const value = typeof byte === 'string' ? parseInt(byte, 16) : byte
    if (!isNaN(value) && value >= 0 && value <= 0xFF) {
      sum += value
    }
  }

  const sum16bit = sum & 0xFFFF
  const checksum = (~sum16bit + 1) & 0xFF

  return checksum.toString(16).padStart(2, '0').toUpperCase()
}

/**
 * Build complete command frame
 */
function buildFrame(command: number, data: number[] = []): Uint8Array {
  // 对齐旧版: LENGTH 等于整帧字节长度 (AA 53 LENGTH CMD DATA... CHECKSUM)
  // 例如 mute: AA 53 08 54 00 01 01 xx (总长 8)
  const length = data.length + 5 // header(2) + length(1) + command(1) + data(n) + checksum(1)
  const frameData = [length, command, ...data]
  const checksum = calculateChecksum(frameData)
  const checksumValue = parseInt(checksum, 16)

  return new Uint8Array([...FRAME_HEADER, ...frameData, checksumValue])
}

/**
 * Convert float32 to 4 bytes (little-endian)
 * Used for EQ frequency, gain, Q value encoding
 */
function float32ToBytes(value: number): number[] {
  const buffer = new ArrayBuffer(4)
  const view = new DataView(buffer)
  view.setFloat32(0, value, true) // true = little-endian
  return Array.from(new Uint8Array(buffer))
}

/**
 * Build speaker mute command
 * 开: AA 53 08 54 00 01 01 a5
 * 关: AA 53 08 54 00 01 00 a6
 */
export function buildSpeakerMuteCommand(mute: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_SPEAKER_MUTE, [0x00, 0x01, mute ? 0x01 : 0x00])
}

/**
 * Build microphone mute command
 * 开: AA 53 08 55 00 01 01 a4
 * 关: AA 53 08 55 00 01 00 a5
 */
export function buildMicMuteCommand(mute: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_MIC_MUTE, [0x00, 0x01, mute ? 0x01 : 0x00])
}

/**
 * Build speaker volume command
 * Original: sendSliderValue with cmd2='56'
 * Format: AA 53 08 56 00 01 [volume_value] [checksum]
 * Volume range: 0-100
 */
export function buildSpeakerVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SET_SPEAKER_VOLUME, [0x00, 0x01, clampedVolume])
}

/**
 * Build microphone volume command
 * Original: sendSliderValue with cmd2='57'
 * Format: AA 53 08 57 00 01 [volume_value] [checksum]
 * Volume range: 0-100
 */
export function buildMicVolumeCommand(volume: number): Uint8Array {
  const clampedVolume = Math.max(0, Math.min(100, Math.round(volume)))
  return buildFrame(DeviceCommand.SET_MIC_VOLUME, [0x00, 0x01, clampedVolume])
}

/**
 * Build anti-interference command
 * 开: AA 53 08 58 00 01 01 a1
 * 关: AA 53 08 58 00 01 00 a2
 */
export function buildAntiInterferenceCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_ANTI_INTERFERENCE, [0x00, 0x01, enabled ? 0x01 : 0x00])
}

/**
 * Build ENC (AI降噪) command
 * Original from server-1.js: AA 53 09 51 00 02 [on/off] 0{level} [checksum]
 * Level range: 1-6 (旧版是1-6档，Vue3 UI需要对齐)
 *
 * @param enabled - 开关状态
 * @param level - 档位 1-6
 */
export function buildEncCommand(enabled: boolean, level: number): Uint8Array {
  const clampedLevel = Math.max(1, Math.min(6, Math.round(level)))
  // 格式: length=09, cmd=51, 00, 02, [on/off], [level], checksum
  return buildFrame(DeviceCommand.SET_ENC, [0x00, 0x02, enabled ? 0x01 : 0x00, clampedLevel])
}

/**
 * Build RGB light effect command
 * Original: AA 53 08 60 01 01 [mode] [brightness]
 * Simplified version - basic on/off with color
 */
export function buildRgbCommand(enabled: boolean, mode: number = 0): Uint8Array {
  // 对齐旧版 server-1.js: AA 53 08 60 01 01 [mode] [checksum]
  // 备注: 旧版协议该命令不带 brightness 字节
  return buildFrame(DeviceCommand.SET_RGB, [0x01, 0x01, enabled ? mode : 0x00])
}

/**
 * Build power on/off command
 * 关机: AA 53 08 70 00 01 00 8A
 * 开机: AA 53 08 70 00 01 01 89
 */
export function buildPowerCommand(powerOn: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_POWER, [0x00, 0x01, powerOn ? 0x01 : 0x00])
}

/**
 * Build voice effect (魔幻音效) command
 * Types: 0=normal, 1=boy, 2=girl, 3=child, 4=robot, 5=monster
 */
export function buildVoiceEffectCommand(type: number): Uint8Array {
  return buildFrame(DeviceCommand.SET_VOICE_EFFECT, [0x00, 0x01, type])
}

/**
 * Build audio split/mix commands
 */
export function buildAudioSplitCommand(enabled: boolean): Uint8Array {
  return buildFrame(DeviceCommand.SET_AUDIO_SPLIT, [0x01, 0x01, enabled ? 0x01 : 0x00])
}

/**
 * Build audio balance command
 * Position value: 0-100 (UI), mapped to 0-200 (protocol)
 * 最左 => 0, 中间 => 100, 最右 => 200
 */
export function buildAudioBalanceCommand(positionValue: number): Uint8Array {
  const sendValue = Math.max(0, Math.min(200, Math.round(positionValue * 2)))
  return buildFrame(DeviceCommand.SET_AUDIO_BALANCE, [0x00, 0x01, sendValue])
}

// ============ EQ Commands (Complex Protocol) ============

/**
 * EQ Data Point - 单个频点数据
 * Aligned with original eq_setting.js structure
 */
export interface EqDataPoint {
  fid: number      // 频点ID (1-14)
  freq: string     // 频率显示文本 (如 '25HZ', '1K')
  gain: number     // 增益值 (-12 到 +12 dB)
  q: string        // Q值 (如 '0.75')
}

/**
 * Build single-point EQ command (实时单点调节)
 * Module 0x52, SubMode 0x02 + bandIndex
 * Format for single point: uses float32 little-endian
 *
 * @param bandIndex - 频段索引 (0-13)
 * @param point - EQ数据点
 */
export function buildEqSinglePointCommand(bandIndex: number, point: EqDataPoint): Uint8Array {
  const subModule = DeviceCommand.EQ_SUBMODE_SINGLE
  const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
  const qValue = parseFloat(point.q)

  // Data: subModule(1) + bandIndex(1) + freq(4 bytes float32) + gain(4 bytes float32) + q(4 bytes float32)
  const data = [
    subModule,
    bandIndex,
    ...float32ToBytes(freqValue),
    ...float32ToBytes(point.gain),
    ...float32ToBytes(qValue),
  ]

  return buildFrame(DeviceCommand.EQ_MODULE, data)
}

/**
 * Build segment EQ command (分段发送 - 完整曲线)
 * 14段按4个segment分段发送: 0x15/0x16/0x17/0x18
 *
 * Segment mapping:
 * - 0x15: indices 0-3 (频点 0,1,2,3)
 * - 0x16: indices 4-7 (频点 4,5,6,7)
 * - 0x17: indices 8-11 (频点 8,9,10,11)
 * - 0x18: indices 12-13 (频点 12,13)
 *
 * @param segmentIndex - 段索引 (0-3)
 * @param points - 该段的所有频点数据
 */
export function buildEqSegmentCommand(segmentIndex: number, points: EqDataPoint[]): Uint8Array {
  const subModules = [
    DeviceCommand.EQ_SUBMODE_SEGMENT1,
    DeviceCommand.EQ_SUBMODE_SEGMENT2,
    DeviceCommand.EQ_SUBMODE_SEGMENT3,
    DeviceCommand.EQ_SUBMODE_SEGMENT4,
  ]

  const subModule = subModules[segmentIndex]
  if (subModule === undefined) {
    throw new Error(`Invalid segment index: ${segmentIndex}`)
  }

  // Data: subModule(1) + [freq(4) + gain(4) + q(4)] * pointCount
  const data: number[] = [subModule]

  for (const point of points) {
    const freqValue = parseFloat(point.freq.replace(/[HzK]/gi, '')) * (point.freq.includes('K') ? 1000 : 1)
    const qValue = parseFloat(point.q)
    data.push(...float32ToBytes(freqValue))
    data.push(...float32ToBytes(point.gain))
    data.push(...float32ToBytes(qValue))
  }

  return buildFrame(DeviceCommand.EQ_MODULE, data)
}

/**
 * Build complete EQ curve commands (all 4 segments)
 * Returns array of 4 commands that should be sent sequentially with delays
 */
export function buildEqCurveCommands(allPoints: EqDataPoint[]): Uint8Array[] {
  // Split 14 points into 4 segments
  const segments = [
    allPoints.slice(0, 4),   // indices 0-3
    allPoints.slice(4, 8),   // indices 4-7
    allPoints.slice(8, 12),  // indices 8-11
    allPoints.slice(12, 14), // indices 12-13
  ]

  return segments.map((points, index) => buildEqSegmentCommand(index, points))
}

/**
 * Build EQ enable/disable command
 * Based on original project EQ toggle behavior
 */
export function buildEqEnableCommand(enabled: boolean): Uint8Array {
  // Simplified - actual implementation depends on device protocol
  // This is a placeholder for the specific enable/disable command
  return buildFrame(DeviceCommand.EQ_MODULE, [0x01, 0x01, enabled ? 0x01 : 0x00])
}

// ============ Command Presets for Direct Use ============

export const COMMAND_PRESETS = {
  // Speaker mute
  SPEAKER_MUTE_ON: 'AA 53 08 54 00 01 01 A5',
  SPEAKER_MUTE_OFF: 'AA 53 08 54 00 01 00 A6',

  // Mic mute
  MIC_MUTE_ON: 'AA 53 08 55 00 01 01 A4',
  MIC_MUTE_OFF: 'AA 53 08 55 00 01 00 A5',

  // Anti-interference
  ANTI_INTERFERENCE_ON: 'AA 53 08 58 00 01 01 A1',
  ANTI_INTERFERENCE_OFF: 'AA 53 08 58 00 01 00 A2',

  // Status queries
  GET_STATUS: 'AA 53 08 80 00 01 00 7A',
  GET_DEVICE_DETAIL: 'AA 53 08 82 00 01 00 78',
} as const

/**
 * Parse command hex string to Uint8Array
 */
export function parseCommandHex(hexString: string): Uint8Array {
  return hexStringToBytes(hexString)
}

/**
 * Validate command format
 */
export function validateCommand(data: Uint8Array): boolean {
  if (data.length < 8) return false
  if (data[0] !== FRAME_HEADER[0] || data[1] !== FRAME_HEADER[1]) return false

  // Verify checksum
  const payload = Array.from(data.slice(2, -1))
  const receivedChecksum = data[data.length - 1]
  const calculatedChecksum = parseInt(calculateChecksum(payload), 16)

  return receivedChecksum === calculatedChecksum
}
